import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { unlockService } from '../services/unlockService'
import { ProjectUnlock, UnlocksQueryParams } from '../types/unlocks'
import { dataRoomService } from '@/services/dataRoomService';

export const useUnlocks = (params?: UnlocksQueryParams) => {
  const [unlocks, setUnlocks] = useState<ProjectUnlock[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const paramsRef = useRef(params)

  // Memoize params to create stable reference
  const stableParams = useMemo(() => params, [
    params?.limit,
    params?.offset,
    params?.search,
    params?.sort_by,
    params?.sort_direction
  ])

  const fetchUnlocks = useCallback(async (page = 1, customParams?: UnlocksQueryParams) => {
    try {
      setLoading(true)
      setError(null)
      
      const activeParams = customParams || stableParams
      const limit = activeParams?.limit || 10
      const offset = (page - 1) * limit
      
      // Send all API-supported parameters
      const queryParams: UnlocksQueryParams = {
        limit,
        offset,
        search: activeParams?.search,
        sort_by: activeParams?.sort_by,
        sort_direction: activeParams?.sort_direction
      }
      
      const response = await unlockService.getUnlocks(queryParams)
      
      setUnlocks(response.data)
      setTotal(response.total)
      setCurrentPage(page)
      setTotalPages(Math.ceil(response.total / limit))
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch unlocks')
    } finally {
      setLoading(false)
    }
  }, [stableParams])

  // API handles sorting, so return unlocks directly
  useEffect(() => {
    // Only refetch if params actually changed
    const paramsChanged = JSON.stringify(paramsRef.current) !== JSON.stringify(stableParams)
    if (!paramsChanged && unlocks.length > 0) {
      return
    }
    
    paramsRef.current = stableParams
    fetchUnlocks(1)
  }, [stableParams, fetchUnlocks, unlocks.length])

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages && !loading) {
      fetchUnlocks(page)
    }
  }, [totalPages, loading, fetchUnlocks])

  const refresh = useCallback(() => {
    fetchUnlocks(currentPage)
  }, [fetchUnlocks, currentPage])

  return {
    unlocks,
    loading,
    error,
    total,
    currentPage,
    totalPages,
    goToPage,
    refresh
  }
}

export * from './useUnlocks';

export function useUnlocksStats() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    dataRoomService.getUnlocksStats()
      .then(setData)
      .catch((e) => setError(e.message || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  return { data, loading, error };
}


